/**
 * @typedef {Window & { __realizeViewportFixes?: number }} RealizeWindow
 */

const REALIZE_EMBED_SOURCE_APP = "realize-configurator";
const REALIZE_EMBED_SOURCE_SHOPIFY = "realize-shopify";
const REALIZE_EMBED_TYPE_NAVIGATE = "navigate";
const REALIZE_EMBED_TYPE_DOCUMENT_METADATA = "document-metadata";
const REALIZE_EMBED_TYPE_READY = "ready";
const REALIZE_EMBED_TYPE_LOADING = "loading";
const REALIZE_EMBED_TYPE_CHECKOUT_REDIRECT = "checkout-redirect";
const REALIZE_EMBED_TYPE_HEADER_HEIGHT = "header-height";

const REALIZE_STORE_COLLECTION_PREFIX = "/collections/";
const REALIZE_STORE_PRODUCT_PREFIX = "/products/";

/**
 * @typedef {Object} EmbedContext
 * @property {string} collectionHandle
 * @property {string} productHandle
 * @property {string} pageType
 * @property {string} storeBasePath
 */

/**
 * @typedef {Object} DocumentMetadata
 * @property {string} [source]
 * @property {string} [type]
 * @property {string} [pathname]
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [url]
 * @property {string} [canonical]
 * @property {string} [image]
 * @property {string} [ogImage]
 */

/** @param {string} pathname */
const normalizePath = (pathname) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

/** @param {string} pathname */
const splitPathSegments = (pathname) =>
  normalizePath(pathname).split("/").filter(Boolean);

/**
 * @param {{ dataset: DOMStringMap }} root
 * @returns {EmbedContext}
 */
const readEmbedContext = (root) => ({
  collectionHandle: root.dataset.collectionHandle || "",
  productHandle: root.dataset.productHandle || "",
  pageType: root.dataset.pageType || "",
  storeBasePath: root.dataset.storeBasePath || "/",
});

/**
 * @param {string} appPath
 * @param {EmbedContext} context
 */
const appPathToStorePath = (appPath, context) => {
  const normalizedAppPath = normalizePath(appPath);
  const segments = splitPathSegments(normalizedAppPath);

  if (normalizedAppPath === "/") {
    return "/";
  }

  if (normalizedAppPath === "/checkout") {
    return "/checkout";
  }

  if (segments.length === 2) {
    return `${REALIZE_STORE_PRODUCT_PREFIX}${segments[1]}`;
  }

  if (segments.length === 1) {
    return null;
  }

  return context.storeBasePath || "/";
};

/**
 * @param {string} handle
 * @param {EmbedContext} [context]
 */
const resolveSingleSegmentStorePath = async (
  handle,
  context = readEmbedContext({ dataset: /** @type {DOMStringMap} */ ({}) }),
) => {
  if (!handle) {
    return "/";
  }

  // Use the known page context first to avoid an unnecessary /products/{handle}.js
  // request (which logs a 404 in the console when the handle is a collection).
  if (
    handle === context.collectionHandle ||
    context.pageType === "collection"
  ) {
    return `${REALIZE_STORE_COLLECTION_PREFIX}${handle}`;
  }

  if (handle === context.productHandle || context.pageType === "product") {
    return `${REALIZE_STORE_PRODUCT_PREFIX}${handle}`;
  }

  try {
    const response = await fetch(
      `${REALIZE_STORE_PRODUCT_PREFIX}${encodeURIComponent(handle)}.js`,
      {
        credentials: "same-origin",
      },
    );

    if (response.ok) {
      return `${REALIZE_STORE_PRODUCT_PREFIX}${handle}`;
    }
  } catch {
    // Fall back to collection URL when product lookup fails.
  }

  return `${REALIZE_STORE_COLLECTION_PREFIX}${handle}`;
};

/**
 * @param {string} storePath
 * @param {EmbedContext} context
 */
const storePathToAppPath = (storePath, context) => {
  const normalizedStorePath = normalizePath(storePath);

  if (normalizedStorePath === "/") {
    return "/";
  }

  if (normalizedStorePath === "/checkout") {
    return "/checkout";
  }

  if (normalizedStorePath.startsWith(REALIZE_STORE_COLLECTION_PREFIX)) {
    const rest = normalizedStorePath.slice(
      REALIZE_STORE_COLLECTION_PREFIX.length,
    );
    const parts = rest.split("/").filter(Boolean);

    if (parts.length >= 3 && parts[1] === "products") {
      return `/${parts[0]}/${parts[2]}`;
    }

    if (parts.length >= 1) {
      return `/${parts[0]}`;
    }

    return "/";
  }

  if (normalizedStorePath.startsWith(REALIZE_STORE_PRODUCT_PREFIX)) {
    const productHandle = normalizedStorePath
      .slice(REALIZE_STORE_PRODUCT_PREFIX.length)
      .split("/")[0];
    const collectionHandle = context.collectionHandle;

    if (collectionHandle && productHandle) {
      return `/${collectionHandle}/${productHandle}`;
    }

    return productHandle ? `/${productHandle}` : "/";
  }

  const normalizedBasePath = normalizePath(context.storeBasePath);

  if (normalizedStorePath === normalizedBasePath) {
    return "/";
  }

  if (
    normalizedBasePath !== "/" &&
    normalizedStorePath.startsWith(`${normalizedBasePath}/`)
  ) {
    return normalizedStorePath.slice(normalizedBasePath.length) || "/";
  }

  return "/";
};

/** @param {unknown} value */
const resolveConfiguratorOrigin = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

/**
 * @param {MessageEvent} event
 * @param {HTMLIFrameElement} iframe
 * @param {string | null} configuratorOrigin
 */
const isConfiguratorMessage = (event, iframe, configuratorOrigin) => {
  if (event.source !== iframe.contentWindow) {
    return false;
  }

  if (configuratorOrigin && event.origin !== configuratorOrigin) {
    return false;
  }

  return event.data?.source === REALIZE_EMBED_SOURCE_APP;
};

/** @param {unknown} data */
const isNavigateMessage = (data) => {
  if (!data || typeof data !== "object") {
    return false;
  }

  const payload = /** @type {DocumentMetadata} */ (data);

  return (
    payload.type === REALIZE_EMBED_TYPE_NAVIGATE &&
    payload.source === REALIZE_EMBED_SOURCE_APP &&
    typeof payload.pathname === "string" &&
    payload.pathname.startsWith("/")
  );
};

/** @param {unknown} data */
const isDocumentMetadataMessage = (data) => {
  if (!data || typeof data !== "object") {
    return false;
  }

  const payload = /** @type {DocumentMetadata} */ (data);

  return (
    payload.type === REALIZE_EMBED_TYPE_DOCUMENT_METADATA &&
    payload.source === REALIZE_EMBED_SOURCE_APP
  );
};

/** @param {unknown} value */
const resolveAbsoluteUrl = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return null;
  }
};

/**
 * @param {string} selector
 * @param {Record<string, string>} attributes
 */
const upsertMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

/** @param {unknown} metadata */
const applyDocumentMetadata = (metadata) => {
  if (!metadata || typeof metadata !== "object") {
    return;
  }

  const payload = /** @type {DocumentMetadata} */ (metadata);

  if (typeof payload.title === "string" && payload.title.trim()) {
    document.title = payload.title.trim();
  }

  if (typeof payload.description === "string") {
    upsertMetaTag('meta[name="description"]', {
      name: "description",
      content: payload.description,
    });
  }

  const canonicalUrl = resolveAbsoluteUrl(payload.url || payload.canonical);

  if (canonicalUrl) {
    let canonical = document.head.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", canonicalUrl);
    upsertMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
  }

  if (typeof payload.title === "string" && payload.title.trim()) {
    upsertMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: payload.title.trim(),
    });
    upsertMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: payload.title.trim(),
    });
  }

  if (typeof payload.description === "string") {
    upsertMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: payload.description,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: payload.description,
    });
  }

  const imageUrl = resolveAbsoluteUrl(payload.image || payload.ogImage);

  if (imageUrl) {
    upsertMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: imageUrl,
    });
    upsertMetaTag('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: imageUrl,
    });
  }
};

/** @param {unknown} value */
const stripHtml = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const template = document.createElement("template");
  template.innerHTML = value;
  return template.content.textContent?.trim() || "";
};

/** @param {string} productHandle */
const fetchAndApplyProductMetadata = async (productHandle) => {
  if (!productHandle) {
    return;
  }

  try {
    const response = await fetch(
      `${REALIZE_STORE_PRODUCT_PREFIX}${encodeURIComponent(productHandle)}.js`,
    );

    if (!response.ok) {
      return;
    }

    const product = await response.json();

    applyDocumentMetadata({
      title: product.title,
      description: stripHtml(product.description),
      url: `${REALIZE_STORE_PRODUCT_PREFIX}${product.handle}`,
      image: product.featured_image,
    });
  } catch {
    // Ignore network errors; configurator may send document-metadata separately.
  }
};

/**
 * @param {HTMLIFrameElement} iframe
 * @param {string} pathname
 */
const postNavigateToIframe = (iframe, pathname) => {
  iframe.contentWindow?.postMessage(
    {
      source: REALIZE_EMBED_SOURCE_SHOPIFY,
      type: REALIZE_EMBED_TYPE_NAVIGATE,
      pathname,
    },
    "*",
  );
};

/**
 * Resolve the store header element the embedded configurator should offset for.
 * The theme renders it as <header is="custom-header"> inside .header-section /
 * #shopify-section-*; fall back to the plain <header> tag.
 * @returns {HTMLElement | null}
 */
const resolveStoreHeaderElement = () => {
  const candidate =
    document.querySelector(".header-section header") ||
    document.querySelector("header.header") ||
    document.querySelector('header[is="custom-header"]') ||
    document.querySelector("header");

  return candidate instanceof HTMLElement ? candidate : null;
};

/**
 * The theme computes the header height itself: theme.js (custom-header.setHeight)
 * writes --header-height onto <html> and keeps it current. Prefer that value over
 * measuring the element here, because it is the same number the rest of the theme
 * lays out against, and it stays correct while the sticky header animates.
 * Falls back to measuring the element when the variable is not set.
 * @returns {number}
 */
const measureStoreHeaderHeight = () => {
  const declared = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
  );

  if (Number.isFinite(declared) && declared > 0) {
    return Math.round(declared);
  }

  const header = resolveStoreHeaderElement();

  if (!header) {
    return 0;
  }

  const height = Math.round(header.getBoundingClientRect().height);
  return Number.isFinite(height) && height > 0 ? height : 0;
};

/**
 * @param {HTMLIFrameElement} iframe
 * @param {number} height
 */
const postHeaderHeightToIframe = (iframe, height) => {
  iframe.contentWindow?.postMessage(
    {
      source: REALIZE_EMBED_SOURCE_SHOPIFY,
      type: REALIZE_EMBED_TYPE_HEADER_HEIGHT,
      height,
    },
    "*",
  );
};

// iOS (iPhone/iPod, and iPadOS which reports as Mac but has touch).
const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iP(hone|od|ad)/.test(navigator.platform || "") ||
    /iP(hone|od|ad)/.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1));

// Safari only: every other iOS browser is WebKit wearing its own chrome (CriOS,
// FxiOS, EdgiOS, ...). The correction below deliberately scrolls the document, which
// Safari needs to unstick a frozen viewport but which shifts the page in Chrome —
// there the viewport is never frozen, only shorter, so there is nothing to unstick.
const IS_IOS_SAFARI =
  IS_IOS && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/.test(navigator.userAgent || "");

const reinitConfiguratorViewport = () => {
  if (!IS_IOS || !document.body) return;

  // Non-Safari iOS browsers need the document left scrollable or their chrome stays
  // pinned open; the CSS keyed off this attribute does that. Nothing below applies to
  // them — their viewport is never frozen, only kept short, and running the scroll
  // correction there is what shifted the page.
  if (!IS_IOS_SAFARI) {
    document.documentElement.setAttribute("data-realize-scroll-unlock", "");
    return;
  }

  const docEl = document.documentElement;
  const body = document.body;

  const isTyping = () => {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName;
    return (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      /** @type {HTMLElement} */ (el).isContentEditable === true
    );
  };

  /**
   * How far the viewport is out of step with the layout, by either symptom.
   *
   * offsetTop covers a visual viewport that has drifted off the layout viewport.
   * innerHeight - clientHeight covers the frozen cold load, where the window reports
   * the full screen while the layout stays stuck short.
   *
   * Deliberately not window.scrollY: the document now carries scroll slack so Chrome's
   * chrome stays collapsible, and an ordinary swipe puts scrollY above zero. Counting
   * that as drift let a plain scroll trigger the correction, and the correction itself
   * scrolls the page — the fix became the shift.
   */
  const gap = () => {
    if (isTyping()) return 0;

    const viewport = window.visualViewport;
    const drift = viewport ? Math.round(viewport.offsetTop) : 0;

    // Safari: the layout viewport stays stuck short of the window it reports.
    const frozen = window.innerHeight - document.documentElement.clientHeight;

    // Chrome, Edge, Firefox: innerHeight and clientHeight agree, so the check above
    // is blind there. What gives them away is how much of the screen never reaches
    // the page — their own chrome accounts for ~150px, and anything past that is the
    // viewport sitting off-screen. Measured: 146px healthy against 176px displaced.
    return Math.max(drift, frozen);
  };

  let settled = false;
  let running = false;

  const attempt = () => {
    if (settled || running) return;

    if (gap() <= 2) {
      return;
    }

    running = true;
    /** @type {RealizeWindow} */ (window).__realizeViewportFixes = (/** @type {RealizeWindow} */ (window).__realizeViewportFixes || 0) + 1;

    // Snapshot the page's own inline styles. If a previous run somehow left its
    // 200vh scaffolding behind, capturing that as "previous" would make it permanent,
    // so treat the scaffolding values as empty.
    const own = (/** @type {string} */ value) =>
      value === "200vh" || value === "auto" ? "" : value;

    const prev = {
      htmlHeight: own(docEl.style.height),
      htmlMinH: own(docEl.style.minHeight),
      htmlOverflowY: own(docEl.style.overflowY),
      bodyHeight: own(body.style.height),
      bodyMinH: own(body.style.minHeight),
      bodyOverflowY: own(body.style.overflowY),
    };

    docEl.style.setProperty("height", "auto", "important");
    docEl.style.setProperty("min-height", "200vh", "important");
    docEl.style.setProperty("overflow-y", "auto", "important");
    body.style.setProperty("height", "auto", "important");
    body.style.setProperty("min-height", "200vh", "important");
    body.style.setProperty("overflow-y", "auto", "important");
    void docEl.offsetHeight;

    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;

      /**
       * @param {HTMLElement} el
       * @param {string} p
       * @param {string} v
       */
      const set = (el, p, v) =>
        v ? el.style.setProperty(p, v) : el.style.removeProperty(p);
      set(docEl, "height", prev.htmlHeight);
      set(docEl, "min-height", prev.htmlMinH);
      set(docEl, "overflow-y", prev.htmlOverflowY);
      set(body, "height", prev.bodyHeight);
      set(body, "min-height", prev.bodyMinH);
      set(body, "overflow-y", prev.bodyOverflowY);
      void docEl.offsetHeight;
      window.scrollTo(0, 0);
      running = false;
      requestAnimationFrame(() => {
        if (gap() <= 2) settled = true;
      });
    };

    window.scrollTo(0, 160);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      requestAnimationFrame(restore);
    });

    // The document must never be left stretched, whatever happens above.
    window.setTimeout(restore, 1200);
  };

  attempt();
  requestAnimationFrame(attempt);
  [80, 200, 400, 700, 1100, 1700, 2400, 3200].forEach((ms) =>
    window.setTimeout(attempt, ms),
  );

  // Chrome on iOS can re-drift when its address bar collapses or expands, so stay
  // armed after startup — but re-arm only when the page has actually drifted again.
  // Clearing `settled` on every viewport event fed the correction its own scrolling
  // and resizing, which ran it in a loop and left the document stretched.
  const viewport = window.visualViewport;
  if (viewport) {
    const onVv = () => {
      if (running) return;
      if (gap() > 2) settled = false;
      attempt();
    };
    viewport.addEventListener("resize", onVv);
    viewport.addEventListener("scroll", onVv);
  }
};

/**
 * Publish the real visible viewport height as --configurator-vh on <html>.
 *
 * 100dvh is correct in Safari but not in the other iOS browsers, which resolve dvh the
 * same as vh and so do not subtract their bottom navigation bar. window.innerHeight
 * reports the height actually available, and on iOS the on-screen keyboard is a layer
 * over the window rather than a resize of it, so it does not collapse while a text
 * field is focused.
 */
const initConfiguratorViewportUnit = () => {
  if (!IS_IOS) return;

  const root = document.documentElement;
  let lastVh = 0;

  const apply = () => {
    const height = window.innerHeight;
    if (!(height > 120 && height < 4000)) return;

    if (Math.abs(height - lastVh) >= 2) {
      lastVh = height;
      root.style.setProperty("--configurator-vh", `${height}px`);
    }
  };

  apply();
  requestAnimationFrame(apply);
  [150, 400, 900, 1500, 2500, 4000].forEach((ms) => window.setTimeout(apply, ms));

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", apply);
    window.visualViewport.addEventListener("scroll", apply);
  }
  window.addEventListener("resize", apply);
  window.addEventListener("orientationchange", () => {
    apply();
    window.setTimeout(apply, 300);
  });
  window.addEventListener("pageshow", apply);
};

const initConfiguratorEmbeds = () => {
  reinitConfiguratorViewport();
  initConfiguratorViewportUnit();

  document.querySelectorAll("[data-configurator-embed]").forEach((root) => {
    if (!(root instanceof HTMLElement)) {
      return;
    }

    if (root.dataset.configuratorEmbedReady === "true") {
      return;
    }

    const src = root.dataset.embedSrc;
    const title = root.dataset.iframeTitle || "Realize 3D product configurator";
    const loader = root.querySelector("[data-configurator-loader]");
    const context = readEmbedContext(root);
    const configuratorOrigin = resolveConfiguratorOrigin(
      root.dataset.configuratorOrigin,
    );

    if (!src || !loader) return;

    root.dataset.configuratorEmbedReady = "true";

    let iframeSrc = src;
    const initialAppPath = storePathToAppPath(
      window.location.pathname,
      context,
    );
    let appReadyReceived = false;

    if (initialAppPath !== "/") {
      const initialUrl = new URL(src);

      initialUrl.pathname = initialAppPath;
      iframeSrc = initialUrl.toString();
    }

    // Share-links land the customer on the store product page with ?share=<id>.
    // Forward the id into the iframe src so the configurator restores that design.
    // The id is left in the store URL so a reload restores it again; commitStoreNavigation
    // strips it from the search it carries forward, so it never leaks onto other products.
    const shareId = new URLSearchParams(window.location.search).get("share");

    if (shareId) {
      const iframeUrl = new URL(iframeSrc);
      iframeUrl.searchParams.set("share", shareId);
      iframeSrc = iframeUrl.toString();
    }

    const iframe = document.createElement("iframe");

    iframe.className = "configurator-embed__frame";
    iframe.dataset.configuratorFrame = "";
    iframe.src = iframeSrc;
    iframe.title = title;
    iframe.loading = "eager";
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = "strict-origin-when-cross-origin";

    let lastSentHeaderHeight = -1;

    /**
     * @param {boolean} [force] Re-send even when the height has not changed. The app
     *   mounts its receiver during hydration, well after the first sync fires, so a
     *   height posted early is simply dropped. Without a forced re-send the value is
     *   never sent again (it has not changed), and the iframe keeps its 0 default.
     */
    const syncHeaderHeight = (force) => {
      const height = measureStoreHeaderHeight();

      if (!force && height === lastSentHeaderHeight) {
        return;
      }

      lastSentHeaderHeight = height;
      postHeaderHeightToIframe(iframe, height);
    };

    const scheduleHeaderHeightSync = () => {
      requestAnimationFrame(() => syncHeaderHeight(false));
    };

    // Covers the whole hydration window: whichever tick the app listener comes up
    // on, one of these lands after it.
    const resendHeaderHeight = () => {
      [0, 100, 300, 600, 1200, 2500, 5000].forEach((ms) =>
        window.setTimeout(() => syncHeaderHeight(true), ms),
      );
    };

    const hideLoader = () => {
      loader.classList.add("is-hidden");
      loader.setAttribute("aria-hidden", "true");
    };

    const showLoader = () => {
      loader.classList.remove("is-hidden");
      loader.setAttribute("aria-hidden", "false");
    };

    /**
     * @param {string} appPath
     * @param {string | null} [storePath]
     */
    const rememberAppPathContext = (appPath, storePath = null) => {
      const segments = splitPathSegments(normalizePath(appPath));

      if (segments.length === 2) {
        context.collectionHandle = segments[0] || "";
        context.productHandle = segments[1] || "";
        root.dataset.collectionHandle = segments[0] || "";
        root.dataset.productHandle = segments[1] || "";
        return;
      }

      if (segments.length !== 1) {
        return;
      }

      const handle = segments[0] || "";
      const resolvedStorePath = storePath || "";

      if (resolvedStorePath.startsWith(REALIZE_STORE_PRODUCT_PREFIX)) {
        context.productHandle = handle;
        root.dataset.productHandle = handle;
        return;
      }

      if (resolvedStorePath.startsWith(REALIZE_STORE_COLLECTION_PREFIX)) {
        context.collectionHandle = handle;
        context.productHandle = "";
        root.dataset.collectionHandle = handle;
        root.dataset.productHandle = "";
      }
    };

    const buildCheckoutSearch = () => {
      const params = new URLSearchParams(window.location.search);
      [
        "preview_theme_id",
        "embedded",
        "shop",
        "host",
        "shop_origin",
        "share",
      ].forEach((key) => params.delete(key));
      const search = params.toString();
      return search ? `?${search}` : "";
    };

    /**
     * @param {string} nextPath
     * @param {string} normalizedAppPath
     * @param {{ replace?: boolean }} [options]
     */
    const commitStoreNavigation = (
      nextPath,
      normalizedAppPath,
      { replace = false } = {},
    ) => {
      if (normalizedAppPath === "/checkout") {
        const checkoutUrl = `${nextPath}${buildCheckoutSearch()}${window.location.hash}`;
        window.location.assign(checkoutUrl);
        return;
      }

      const currentPath = normalizePath(window.location.pathname);
      const state = { realizeConfigurator: true, appPath: normalizedAppPath };
      const isSamePath = currentPath === normalizePath(nextPath);

      // Carry the current search forward, but only keep ?share= while we stay on the
      // page it landed on (so a reload still restores it). Moving to another product
      // drops it — a stale share id must never re-apply to a different design.
      const searchParams = new URLSearchParams(window.location.search);
      if (!isSamePath) {
        searchParams.delete("share");
      }
      const search = searchParams.toString();
      const nextUrl = `${nextPath}${search ? `?${search}` : ""}${window.location.hash}`;

      if (isSamePath) {
        if (window.history.state?.appPath !== normalizedAppPath) {
          window.history.replaceState(state, "", nextUrl);
        }

        return;
      }

      if (replace) {
        window.history.replaceState(state, "", nextUrl);
      } else {
        window.history.pushState(state, "", nextUrl);
      }
    };

    /**
     * @param {string} appPath
     * @param {{ replace?: boolean }} [options]
     */
    const syncStoreUrl = async (appPath, { replace = false } = {}) => {
      const normalizedAppPath = normalizePath(appPath);
      const appSegments = splitPathSegments(normalizedAppPath);
      let nextPath = appPathToStorePath(appPath, context);

      if (nextPath == null && appSegments.length === 1) {
        nextPath = await resolveSingleSegmentStorePath(
          appSegments[0] || "",
          context,
        );
        rememberAppPathContext(appPath, nextPath);
      } else {
        rememberAppPathContext(appPath, nextPath);
      }

      if (nextPath == null) {
        nextPath = "/";
      }

      if (appSegments.length === 2) {
        void fetchAndApplyProductMetadata(appSegments[1] || "");
      } else if (nextPath.startsWith(REALIZE_STORE_PRODUCT_PREFIX)) {
        const productHandle =
          nextPath.slice(REALIZE_STORE_PRODUCT_PREFIX.length).split("/")[0] ||
          "";
        void fetchAndApplyProductMetadata(productHandle);
      }

      commitStoreNavigation(nextPath, normalizedAppPath, { replace });
    };

    /** @param {PopStateEvent} event */
    const onPopState = (event) => {
      const appPath =
        event.state?.appPath != null
          ? normalizePath(event.state.appPath)
          : storePathToAppPath(window.location.pathname, context);

      if (appPath !== "/") {
        rememberAppPathContext(appPath);
      }

      postNavigateToIframe(iframe, appPath);
    };

    /** @param {MessageEvent} event */
    const onMessage = (event) => {
      if (!isConfiguratorMessage(event, iframe, configuratorOrigin)) {
        return;
      }

      const { data } = event;

      if (data.type === REALIZE_EMBED_TYPE_CHECKOUT_REDIRECT) {
        if (typeof data.url === "string" && data.url) {
          const checkoutUrl = resolveAbsoluteUrl(data.url);

          if (
            checkoutUrl &&
            new URL(checkoutUrl).origin === window.location.origin
          ) {
            window.location.assign(checkoutUrl);
          }
        }
        return;
      }

      if (data.type === REALIZE_EMBED_TYPE_READY) {
        appReadyReceived = true;
        hideLoader();
        // The configurator only mounts its receiver once it is ready; (re)send the
        // current header height so the embedded desktop layout can offset for it.
        syncHeaderHeight(true);
        return;
      }

      if (data.type === REALIZE_EMBED_TYPE_LOADING) {
        showLoader();
        return;
      }

      if (isDocumentMetadataMessage(data)) {
        applyDocumentMetadata(data);
        return;
      }

      if (!isNavigateMessage(data)) {
        return;
      }

      syncStoreUrl(data.pathname);
    };

    iframe.addEventListener("load", resendHeaderHeight);

    root.insertBefore(iframe, loader);
    window.setTimeout(() => {
      if (!appReadyReceived) {
        hideLoader();
      }
    }, 12000);

    window.addEventListener("message", onMessage);
    window.addEventListener("popstate", onPopState);
    window.addEventListener("resize", scheduleHeaderHeightSync);
    window.addEventListener("scroll", scheduleHeaderHeightSync, { passive: true });

    const headerEl = resolveStoreHeaderElement();

    if (headerEl && typeof ResizeObserver !== "undefined") {
      const headerResizeObserver = new ResizeObserver(scheduleHeaderHeightSync);
      headerResizeObserver.observe(headerEl);
    }

    document.addEventListener("header:scrolled", scheduleHeaderHeightSync);

    scheduleHeaderHeightSync();

    if (initialAppPath !== "/") {
      void syncStoreUrl(initialAppPath, { replace: true }).then(() => {
        rememberAppPathContext(initialAppPath, window.location.pathname);
      });
    }
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initConfiguratorEmbeds);
} else {
  initConfiguratorEmbeds();
}
